import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'

import ApartmentIcon from '@mui/icons-material/Apartment'
import HomeWorkIcon from '@mui/icons-material/HomeWork'
import PaymentIcon from '@mui/icons-material/Payment'
import GTranslate from '@mui/icons-material/GTranslate'

import { Box, Chip, Link, Typography, styled, Button } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useCallback, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import ClipboardCopy from 'src/components/CopyToClipboard/CopyToClipboard'
import { env, formatNthNumber, formatPhoneNumber } from 'src/helpers'
import { languageCodeToName } from 'src/pages/Inbox/helpers'
import usePersonInfo from 'src/pages/Contact/Details/hooks/usePersonInfo'
import OfficeHours from './OfficeHours'
import LanguageSelect from './LanguageSelect'
import { Person, Property, Unit_Occupancy } from 'src/@types/graphql'
import { useUser } from 'src/contexts/providers/UserProvider'

export const Container = styled(Box)(({ theme }) => ({
  overflowX: 'hidden',
  overflowY: 'scroll',
  padding: '0 12px',
}))

const StyledBox = styled(Box)(({ theme }) => ({
  paddingRight: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  fontFamily: theme.typography.fontFamily,
}))

const StyledLink = styled(Link)(({ theme }) => ({
  alignItems: 'center',
  textDecoration: 'none',
  display: 'flex',
  color: theme.legacyColors.PRIMARY_ALTERNATE,
  '&:hover': {
    textDecoration: 'underline',
  },
  marginBottom: theme.spacing(1),
}))

const StyledRowBox = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  textDecoration: 'none',
  display: 'flex',
  color: theme.legacyColors.PRIMARY_ALTERNATE,
  marginBottom: theme.spacing(1),
}))

const StyledRouterLink = styled(RouterLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: theme.palette.link.isUnderlined ? 'underline' : 'none',
  color: theme.palette.link.alternateColor,
  '&:hover': {
    textDecoration: theme.palette.link.isUnderlined ? 'underline' : 'none',
  },
  marginBottom: theme.spacing(1),
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}))

const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.legacyColors.PRIMARY_ALTERNATE,
  fontSize: '0.75rem',
  fontWeight: 500,
  textDecoration: 'underline',
  '&:hover': {
    textDecoration: 'underline',
  },
}))

export const ScrollingTags = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: 300,
  scrollBehavior: 'smooth',
  cursor: 'pointer',
  overflow: 'hidden',
  overflowX: 'scroll',
  '& .MuiChip-colorError': {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
  },
  '& .MuiChip-colorSecondary': {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.grey[200],
  },
}))

export const StyledFlexRow = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const getPMOOverrides = (person: Person, oProperty?: Property) => {
  const shouldOverride =
    person?.type === 'PAST_RESIDENT' &&
    oProperty?.enable_post_moveout &&
    oProperty?.account?.enable_post_moveout_product

  const property = { ...oProperty }

  if (shouldOverride) {
    if (property.pmo_override_office_email) {
      property.office_email = property.pmo_override_office_email
    }
    if (property.pmo_override_office_phone) {
      property.office_phone = property.pmo_override_office_phone
    }
    if (property.pmo_override_portal_url) {
      property.portal_url = property.pmo_override_portal_url
    }
  }

  return property
}

export function PropertyInformation({
  property,
  person,
  activeOccupancy,
  canEditContact,
}: {
  property?: Property
  canEditContact: boolean
  person: Person
  activeOccupancy?: Unit_Occupancy
}) {
  const { isSuperAdmin, hasBuzzRegionalManagerRole, hasAccountOwnerRole } =
    useUser()

  const unit = activeOccupancy?.unit
  const canNavigateProperty = hasBuzzRegionalManagerRole || hasAccountOwnerRole
  const canNavigateAccount = isSuperAdmin()
  const hasActiveExternalLink = property?.link?.some((l) => l.is_active)
  const [showLanguageDrawer, setShowLanguageDrawer] = useState(false)
  const { refetch } = usePersonInfo(person.id)
  const pmoOverrides = getPMOOverrides(person, property)
  const toggleLanguageDrawer = useCallback(
    () => setShowLanguageDrawer((prev) => !prev),
    [],
  )

  return (
    <StyledBox>
      <StyledRowBox>
        <ApartmentIcon sx={{ mr: 1 }} />
        <Typography variant="body2">{unit?.name}</Typography>
      </StyledRowBox>
      {canNavigateProperty ? (
        <StyledRouterLink
          to={`/property/${property?.id}/admin`}
          rel="noopener noreferrer"
          target="_blank"
        >
          <HomeWorkIcon sx={{ mr: 1 }} />
          <Typography variant="body2">{property?.name}</Typography>
        </StyledRouterLink>
      ) : (
        <StyledRowBox>
          <HomeWorkIcon sx={{ mr: 1 }} />
          <Typography variant="body2">{property?.name}</Typography>
        </StyledRowBox>
      )}
      <StyledRowBox>
        <ClipboardCopy text={pmoOverrides?.portal_url || ''} type="url">
          <PaymentIcon sx={{ mr: 1 }} />
          {pmoOverrides?.portal_url || ''}
        </ClipboardCopy>
      </StyledRowBox>
      {/* <Typography variant="body2">{property?.full_address || ''}</Typography> */}
      <StyledRowBox>
        <ClipboardCopy text={pmoOverrides?.office_email || ''} type="email">
          <EmailIcon sx={{ mr: 1 }} />
          {pmoOverrides?.office_email || ''}
        </ClipboardCopy>
      </StyledRowBox>
      <StyledRowBox>
        <ClipboardCopy
          text={formatPhoneNumber(pmoOverrides?.office_phone || '')}
          type="phone"
        >
          <PhoneIcon sx={{ mr: 1 }} />
          {formatPhoneNumber(pmoOverrides?.office_phone || '')}
        </ClipboardCopy>
      </StyledRowBox>
      <StyledRowBox>
        <GTranslate sx={{ mr: 1 }} />
        <StyledFlexRow>
          <Typography fontWeight={500} fontSize="0.875rem">
            {languageCodeToName(person.language_code)}
          </Typography>
          {canEditContact && (
            <StyledButton onClick={toggleLanguageDrawer}>Change</StyledButton>
          )}
        </StyledFlexRow>
      </StyledRowBox>
      {hasActiveExternalLink && (
        <StyledRowBox>
          <ClipboardCopy
            text={`${env('web_public_url')}${btoa(String(property?.id))}`}
            type="url"
          >
            {`${env('web_public_url')}${btoa(String(property?.id))}`}
          </ClipboardCopy>
        </StyledRowBox>
      )}
      <OfficeHours property={property} />
      <StyledTypography variant="body2">
        Late Fee Day:{' '}
        {formatNthNumber(
          activeOccupancy?.late_fee_day_of_month! ||
            property?.late_fee_day_of_month!,
        )}
      </StyledTypography>
      {property?.portal_close_day_of_month && (
        <StyledTypography variant="body2">
          Portal Close Day:&nbsp;
          {formatNthNumber(property?.portal_close_day_of_month!)}
        </StyledTypography>
      )}
      {canNavigateAccount ? (
        <StyledRouterLink
          to={`/account/${person?.account?.id}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          <HomeWorkIcon sx={{ mr: 1 }} />
          <Typography variant="body2">{person?.account?.name}</Typography>
        </StyledRouterLink>
      ) : (
        <StyledRowBox>
          <HomeWorkIcon sx={{ mr: 1 }} />
          <Typography variant="body2">{person?.account?.name}</Typography>
        </StyledRowBox>
      )}
      <ScrollingTags>
        {activeOccupancy?.allow_online_payment && (
          <Chip color="success" label="Online Payments" />
        )}
        {activeOccupancy?.is_eviction && (
          <Chip color="error" label="Evictions" />
        )}
        {activeOccupancy?.is_collections && (
          <Chip color="secondary" label="Collections" />
        )}
        {activeOccupancy?.require_payment_full && (
          <Chip color="secondary" label="Full Payments" />
        )}
        {activeOccupancy?.is_cert_funds_only && (
          <Chip color="secondary" label="Certified Funds" />
        )}
      </ScrollingTags>

      {/* Language Selection Drawer */}
      <LanguageSelect
        open={showLanguageDrawer}
        onClose={toggleLanguageDrawer}
        currentLanguageCode={person?.language_code}
        residentId={person?.id}
        refetch={refetch}
      />
    </StyledBox>
  )
}
